import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiRequestResponseService } from '@data-terminal/data-access/api-request-response/api-request-response.service';
import { BACKEND_URL, ORGANIZATION_REQUEST_PREFIX } from '@data-terminal/data-access/injection-tokens';
import { isClientApiError } from '@data-terminal/feature-workstation-details/functions/blocking-error-dialog.function';
import { FeatureOperationServiceModule } from '@data-terminal/feature-workstation-details/operation-service.module';
import { ApiRequestType } from '@data-terminal/shared-models/api-request-type.enum';
import { ClientApiError } from '@data-terminal/shared-models/client-api-error';
import { IOTResponseError } from '@data-terminal/shared-models/datatransfer';
import { OperationActionSignOff } from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import {
    CreateOperation,
    InputStackOccupancy,
    Operation,
    OperationAction,
    OperationActionRequest,
    OperationActionType,
    SetCommentParam,
    SetCounterBody,
    StackOccupancyScope,
    StartOperationActivity,
    StartOperationActivityBody,
} from '@data-terminal/shared-models/operation';
import { StatusActionResponse } from '@data-terminal/shared-models/status-action-response';
import { catchError, EMPTY, map, Observable } from 'rxjs';

@Injectable({
    providedIn: FeatureOperationServiceModule,
})
export class OperationApiService {
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(BACKEND_URL);
    private readonly orgRequestPrefix = inject(ORGANIZATION_REQUEST_PREFIX);
    private readonly apiRequestResponseService = inject(ApiRequestResponseService);

    private readonly DO_START_URL = `${this.backendUrl}${this.orgRequestPrefix}dostart`;

    private readonly OPERATION_LIST_URL: (machineId: string) => string = (machineId) =>
        `${this.backendUrl}${this.orgRequestPrefix}operationlist/${machineId}`;

    private readonly RUNNING_OPERATIONS_URL: (machineId: string) => string = (machineId) =>
        `${this.backendUrl}${this.orgRequestPrefix}runningWorksteps/${machineId}`;

    private readonly OPERATION_BY_KEY_URL: (workstep: string) => string = (workstep) =>
        `${this.backendUrl}${this.orgRequestPrefix}getoperationbykey/${workstep}`;

    private readonly INTERRUPT_OPERATION_URL = `${this.backendUrl}${this.orgRequestPrefix}operationinterrupt`;

    private readonly FINISH_OPERATION_URL = `${this.backendUrl}${this.orgRequestPrefix}operationfinish`;

    private readonly SET_COUNTERS_URL = `${this.backendUrl}${this.orgRequestPrefix}setcounters`;

    private readonly OPERATION_STORAGE_OCCUPANCIES_URL: (primaryKey: string) => string = (primaryKey) =>
        `${this.backendUrl}${this.orgRequestPrefix}operationstorageoccupancies/${encodeURIComponent(primaryKey)}`;

    private readonly ORDER_BAG_URL = this.backendUrl + 'api/session/getOrderbag';

    private readonly PRINT_PALLET_SHEET_URL = `${this.backendUrl}${this.orgRequestPrefix}printpalletsheet`;

    private readonly COMPLETED_OPERATIONLIST_URL: (machineId: string, jobId: string) => string = (machineId, jobId) =>
        `${this.backendUrl}${this.orgRequestPrefix}completedoperationlist/${machineId}/${jobId}`;

    private readonly OPERATIONLIST_BY_CLASS_URL: (machineId: string, jobId: string) => string = (machineId, jobId) =>
        `${this.backendUrl}${this.orgRequestPrefix}operationlistbyclass/${machineId}/${jobId}`;

    private readonly GET_COMMENTS_WORKFLOW: (workstep: string) => string = (workstep) =>
        `${this.backendUrl}${this.orgRequestPrefix}getComment/${workstep}`;

    private readonly OPERATIONLIST_BY_JOBID_URL: (jobId: string) => string = (jobId) =>
        `${this.backendUrl}${this.orgRequestPrefix}operationlistbyjob/${jobId}`;

    private readonly SET_COMMENT_WORKFLOW = `${this.backendUrl}${this.orgRequestPrefix}setComment`;

    private readonly CHECK_OPERATIONLIST_UPDATE: (machineId: string, operationListTime: string) => string = (
        machineId,
        operationListTime
    ) => `${this.backendUrl}${this.orgRequestPrefix}checkOperationListUpdate/${machineId}/${operationListTime}`;

    public getOperationList(machineId: string): Observable<Operation[]> {
        return this.http.get<Operation[]>(this.OPERATION_LIST_URL(machineId));
    }

    public getRunningOperations(machineId: string): Observable<Operation[]> {
        return this.http.get<Operation[]>(this.RUNNING_OPERATIONS_URL(machineId));
    }

    public checkOperationListUpdate(machineId: string, timestamp: string): Observable<boolean> {
        return this.http.get<boolean>(this.CHECK_OPERATIONLIST_UPDATE(machineId, timestamp));
    }

    public startOperationActivity(
        startOperationActivity: StartOperationActivity
    ): Observable<Operation | ClientApiError> {
        return this.startOperationActivityWithMessageIds(startOperationActivity).pipe(
            map((response) => (isClientApiError(response) ? response : response.data))
        );
    }

    public startOperationActivityWithMessageIds(
        startOperationActivity: StartOperationActivity
    ): Observable<StatusActionResponse<Operation> | ClientApiError> {
        const currentRunningOperation = startOperationActivity.currentRunningOperation;

        const body: StartOperationActivityBody = {
            primaryKey: startOperationActivity.operation.primaryKey,
            machineId: startOperationActivity.machineId,
            activityId: startOperationActivity.activity.actId,
            wasteCount: currentRunningOperation?.wasteAmount || 0,
            goodCount: currentRunningOperation?.goodAmount || 0,
            operationAction: this.mapActionType(currentRunningOperation?.actionType),
            type: startOperationActivity.type,
            persistType: startOperationActivity.persistType,
        };
        if (currentRunningOperation?.releaseStorage !== undefined) {
            body.releaseStorage = currentRunningOperation.releaseStorage;
        }
        if (currentRunningOperation?.comment !== null && currentRunningOperation?.comment !== undefined) {
            body.comment = currentRunningOperation.comment;
        }
        return this.http.post<StatusActionResponse<Operation> | ClientApiError>(this.DO_START_URL, body);
    }

    public getOperation(primaryKey: string): Observable<Operation> {
        return this.http.get<Operation>(this.OPERATION_BY_KEY_URL(primaryKey));
    }

    public getOperationStorageOccupancies(
        primaryKey: string,
        scope: StackOccupancyScope
    ): Observable<InputStackOccupancy[]> {
        return this.http.get<InputStackOccupancy[]>(this.OPERATION_STORAGE_OCCUPANCIES_URL(primaryKey), {
            params: new HttpParams({ fromObject: { scope } }),
        });
    }

    public updateOperationAction({
        operationAction,
        primaryKey,
        machineId,
    }: OperationActionRequest): Observable<Operation | ClientApiError> {
        return this.updateOperationActionWithMessageIds({ operationAction, primaryKey, machineId }).pipe(
            map((response) => (isClientApiError(response) ? response : response.data))
        );
    }

    public updateOperationActionWithMessageIds({
        operationAction,
        primaryKey,
        machineId,
    }: OperationActionRequest): Observable<StatusActionResponse<Operation> | ClientApiError> {
        const url = this.getOperationActionUrl(operationAction.actionType);
        const body = this.getSetCounterBody(operationAction, primaryKey, machineId);
        return this.http.post<StatusActionResponse<Operation> | ClientApiError>(url, body);
    }

    public createNewOperation(
        createOperation: CreateOperation
    ): Observable<Operation | IOTResponseError | ClientApiError> {
        return this.apiRequestResponseService
            .sendApiRequest<CreateOperation, Operation | IOTResponseError | ClientApiError>(
                ApiRequestType.OPERATIONCREATE,
                createOperation
            )
            .pipe(map((requestMetadata) => requestMetadata?.data || ({} as Operation)));
    }

    private getSetCounterBody(operationAction: OperationAction, primaryKey: string, machineId: string): SetCounterBody {
        const body: SetCounterBody = {
            machineId: machineId,
            primaryKey: primaryKey,
        };
        if (operationAction.wasteAmount !== null && operationAction.wasteAmount !== undefined) {
            body.wasteCount = operationAction.wasteAmount || 0;
        }
        if (operationAction.goodAmount !== null && operationAction.goodAmount !== undefined) {
            body.goodCount = operationAction.goodAmount || 0;
        }
        if (operationAction.comment !== null && operationAction.comment !== undefined) {
            body.comment = operationAction.comment;
        }
        if (operationAction.releaseStorage !== undefined) {
            body.releaseStorage = operationAction.releaseStorage;
        }
        return body;
    }

    private getOperationActionUrl(actionType: OperationActionType): string {
        switch (actionType) {
            case OperationActionType.PAUSE:
                return this.INTERRUPT_OPERATION_URL;
            case OperationActionType.STOP:
                return this.FINISH_OPERATION_URL;
            case OperationActionType.ADD_REPORT:
                return this.SET_COUNTERS_URL;
        }
        return this.SET_COUNTERS_URL;
    }

    public getOrderBag(primaryKey: string, machineId: string): Observable<string> {
        return this.http.get<string>(this.ORDER_BAG_URL, {
            params: {
                primaryKey,
                machineId,
            },
        });
    }

    private mapActionType(action?: OperationActionType): OperationActionSignOff | undefined {
        switch (action) {
            case OperationActionType.STOP:
                return OperationActionSignOff.FINISH;
            case OperationActionType.PAUSE:
                return OperationActionSignOff.PAUSE;
            default:
                return undefined;
        }
    }

    public printPalletSheet(primaryKey: string, machineId: string, shouldRequestPickup: boolean): Observable<boolean> {
        return this.http.post<boolean>(this.PRINT_PALLET_SHEET_URL, {
            params: {
                primaryKey,
                machineId,
                shouldCreateOutputTask: shouldRequestPickup,
            },
        });
    }

    public completedOperationlist(machineId: string, jobId: string): Observable<Operation[]> {
        return this.http.get<Operation[]>(this.COMPLETED_OPERATIONLIST_URL(machineId, jobId));
    }

    public operationlistByClass(machineId: string, jobId: string): Observable<Operation[]> {
        return this.http.get<Operation[]>(this.OPERATIONLIST_BY_CLASS_URL(machineId, jobId));
    }

    public operationlistByJobId(jobId: string): Observable<Operation[]> {
        return this.http.get<Operation[]>(this.OPERATIONLIST_BY_JOBID_URL(jobId));
    }

    public getComment(workstep: string): Observable<string[]> {
        return this.http.get<string[]>(this.GET_COMMENTS_WORKFLOW(workstep));
    }

    public setComment(commentParam: SetCommentParam): Observable<boolean> {
        return this.http.post<boolean>(this.SET_COMMENT_WORKFLOW, commentParam).pipe(catchError(() => EMPTY));
    }
}
